if (!customElements.get('blj-customization')) {
  customElements.define('blj-customization', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.photo = this.querySelector('[data-custom-photo]');
      this.notes = this.querySelector('[data-custom-notes]');
      this.preview = this.querySelector('[data-photo-preview]');
      this.status = this.querySelector('[data-photo-status]');
      this.draftStatus = this.querySelector('[data-draft-status]');
      this.defaultDraftStatus = this.draftStatus?.textContent || '';
      this.error = this.querySelector('[data-photo-error]');
      this.counter = this.querySelector('[data-note-count]');
      this.form = this.closest('form');
      this.variant = this.form?.querySelector('input[name="id"]');
      this.quantity = this.form?.elements.namedItem('quantity');
      this.revision = 0;
      const signal = this.controller.signal;
      this.photo?.addEventListener('change', () => { this.updatePhoto(); this.edited(0); }, { signal });
      this.notes.addEventListener('input', () => { this.updateCount(); this.edited(250); }, { signal });
      this.quantity?.addEventListener('input', () => this.edited(250), { signal });
      this.variant?.addEventListener('change', () => this.switchDraft(), { signal });
      this.querySelector('[data-remove-photo]')?.addEventListener('click', () => {
        this.photo.value = '';
        this.updatePhoto();
        this.edited(0);
        this.photo.focus();
      }, { signal });
      window.addEventListener('pagehide', () => this.saveCurrentDraft(), { signal });
      this.updatePhoto();
      this.updateCount();
      this.restoreInitialDraft();
    }
    updateCount() { this.counter.textContent = (this.dataset.nameOnly === 'true' ? Array.from(this.notes.value).length : this.notes.value.length) + '/' + (this.dataset.nameOnly === 'true' ? 4 : this.notes.maxLength); }
    updatePhoto() {
      if (!this.photo) { this.status.textContent = this.dataset.noFile; return; }
      if (this.objectURL) URL.revokeObjectURL(this.objectURL);
      this.objectURL = null;
      this.preview.hidden = true;
      const img = this.preview.querySelector('img');
      img.removeAttribute('src');
      const file = this.photo.files[0];
      let message = '';
      if (file && !['image/jpeg','image/png','image/webp'].includes(file.type)) message = this.dataset.fileType;
      else if (file && file.size === 0) message = this.dataset.fileEmpty;
      else if (file && file.size > 10 * 1024 * 1024) message = this.dataset.fileSize;
      this.photo.setCustomValidity(message);
      this.photo.setAttribute('aria-invalid', String(Boolean(message)));
      this.error.textContent = message;
      this.error.hidden = !message;
      this.status.textContent = file && !message ? file.name + ' · ' + this.dataset.localPreview : this.dataset.noFile;
      if (file && !message) {
        this.objectURL = URL.createObjectURL(file);
        img.src = this.objectURL;
        this.preview.hidden = false;
      }
    }
    snapshot() {
      const file = this.photo?.files[0];
      return { notes: this.notes.value, quantity: this.quantity?.value || '1', file: file && !this.photo.validationMessage ? file : null };
    }
    edited(delay) {
      this.revision++;
      this.submitted = false;
      this.requestState = null;
      this.draftStatus.hidden = false;
      this.updatePhoto();
      clearTimeout(this.saveTimer);
      this.saveTimer = setTimeout(() => this.saveCurrentDraft(), delay);
    }
    async restoreInitialDraft() {
      try {
        if (!window.BljCartFlow || !this.variant || !this.dataset.productId) return;
        this.activeVariant = this.variant.value;
        this.savedKey = window.BljCartFlow.draftKey(this.dataset.productId, this.activeVariant);
        await this.restoreDraft(this.savedKey, this.revision);
      } catch { this.showDraftError(); }
    }
    async restoreDraft(key, revision) {
      const saved = await window.BljCartFlow.getDraft(key);
      if (!saved || key !== this.savedKey || revision !== this.revision || !this.isConnected) return;
      this.notes.value = saved.notes || '';
      if (this.quantity) this.quantity.value = saved.quantity || '1';
      if (saved.file && this.photo) {
        const transfer = new DataTransfer();
        transfer.items.add(saved.file);
        this.photo.files = transfer.files;
      }
      this.updateCount();
      this.updatePhoto();
      this.draftStatus.textContent = this.dataset.draftRestored;
    }
    saveCurrentDraft() {
      clearTimeout(this.saveTimer);
      if (!this.savedKey || this.submitted || !window.BljCartFlow) return Promise.resolve();
      const key = this.savedKey, revision = this.revision, value = this.snapshot();
      this.lastSave = window.BljCartFlow.saveDraft(key, value).then(() => {
        if (key === this.savedKey && revision === this.revision && !this.submitted && !['Pending','Unknown','Committed'].includes(this.requestState)) this.draftStatus.textContent = this.dataset.draftSaved;
      }).catch(() => this.showDraftError());
      return this.lastSave;
    }
    async switchDraft() {
      if (!window.BljCartFlow || this.variant.value === this.activeVariant) return;
      const previousKey = this.savedKey, previousValue = this.snapshot(), wasSubmitted = this.submitted;
      clearTimeout(this.saveTimer);
      const next = this.variant.value;
      this.revision++;
      this.activeVariant = next;
      this.savedKey = window.BljCartFlow.draftKey(this.dataset.productId, next);
      this.submitted = false;
      this.notes.value = '';
      if (this.photo) this.photo.value = '';
      if (this.quantity) this.quantity.value = this.quantity.min || this.quantity.dataset.min || '1';
      this.draftStatus.textContent = this.defaultDraftStatus;
      this.updateCount();
      this.updatePhoto();
      const key = this.savedKey, revision = this.revision;
      try {
        if (previousKey && !wasSubmitted) await window.BljCartFlow.saveDraft(previousKey, previousValue);
        await this.restoreDraft(key, revision);
      }
      catch { this.showDraftError(); }
    }
    draftToken() { return { key: this.savedKey, revision: this.revision }; }
    submissionStatus(state) {
      this.requestState = state;
      const message = this.dataset['submission' + state];
      if (message) { this.status.textContent = message; this.draftStatus.hidden = true; }
    }
    async clearSavedDraft(token) {
      if (!token?.key || token.key !== this.savedKey || token.revision !== this.revision) return;
      clearTimeout(this.saveTimer);
      this.submitted = true;
      await this.lastSave;
      try { await window.BljCartFlow.clearDraft(token.key); }
      catch { this.showDraftError(); }
    }
    showDraftError() { if (this.draftStatus) this.draftStatus.textContent = this.dataset.draftError; }
    disconnectedCallback() {
      this.saveCurrentDraft();
      this.controller?.abort();
      this.controller = null;
      if (this.objectURL) URL.revokeObjectURL(this.objectURL);
      this.objectURL = null;
    }
  });
}
